import Phaser from 'phaser'

interface GameStats {
  towersBuilt: number
  unitsSent: number
  creepsKilled: number
  goldEarned: number
  damageDealt: number
}

interface GameOverData {
  winner: 'player' | 'enemy'
  wave: number
  stats?: GameStats
}

export class GameOverScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameOverScene' })
  }

  create(data: GameOverData) {
    const width = this.cameras.main.width
    const height = this.cameras.main.height

    const isVictory = data.winner === 'player'

    // Background overlay
    this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.8)

    // Title
    const title = this.add.text(
      width / 2,
      120,
      isVictory ? 'VICTORY!' : 'DEFEAT',
      {
        fontSize: '72px',
        fontFamily: 'monospace',
        color: isVictory ? '#00ff88' : '#ff4444',
        fontStyle: 'bold'
      }
    )
    title.setOrigin(0.5)

    // Subtitle
    const subtitle = this.add.text(
      width / 2,
      190,
      isVictory ? 'You destroyed the enemy!' : 'Your base was destroyed...',
      {
        fontSize: '20px',
        fontFamily: 'monospace',
        color: '#aaaaaa'
      }
    )
    subtitle.setOrigin(0.5)

    // Wave survived
    const waveText = this.add.text(
      width / 2,
      240,
      `Waves Survived: ${data.wave}`,
      {
        fontSize: '28px',
        fontFamily: 'monospace',
        color: '#ffffff'
      }
    )
    waveText.setOrigin(0.5)

    // Stats panel
    if (data.stats) {
      const statsY = 310
      const statsBoxWidth = 400
      const statsBoxHeight = 180

      // Stats background
      this.add.rectangle(width / 2, statsY + statsBoxHeight / 2, statsBoxWidth, statsBoxHeight, 0x1a1a2e)
        .setStrokeStyle(2, 0x4a90d9)

      this.add.text(width / 2, statsY + 20, 'BATTLE STATISTICS', {
        fontSize: '16px',
        fontFamily: 'monospace',
        color: '#4a90d9',
        fontStyle: 'bold'
      }).setOrigin(0.5)

      const statLines = [
        { label: 'Towers Built', value: data.stats.towersBuilt.toString(), color: '#4a90d9' },
        { label: 'Units Sent', value: data.stats.unitsSent.toString(), color: '#ff6b6b' },
        { label: 'Creeps Killed', value: data.stats.creepsKilled.toString(), color: '#e94560' },
        { label: 'Gold Earned', value: `${data.stats.goldEarned}g`, color: '#ffd700' },
        { label: 'Damage Dealt', value: data.stats.damageDealt.toLocaleString(), color: '#ffffff' }
      ]

      statLines.forEach((stat, index) => {
        const y = statsY + 55 + index * 24

        this.add.text(width / 2 - 150, y, stat.label, {
          fontSize: '14px',
          fontFamily: 'monospace',
          color: '#888888'
        })

        this.add.text(width / 2 + 150, y, stat.value, {
          fontSize: '14px',
          fontFamily: 'monospace',
          color: stat.color
        }).setOrigin(1, 0)
      })
    }

    // Buttons
    const buttonsY = data.stats ? 540 : 400

    this.createButton(
      width / 2 - 120,
      buttonsY,
      'PLAY AGAIN',
      '#00ff88',
      () => this.scene.start('GameScene', { mode: 'single' })
    )

    this.createButton(
      width / 2 + 120,
      buttonsY,
      'MAIN MENU',
      '#4a90d9',
      () => this.scene.start('MenuScene')
    )

    // Tip text
    this.add.text(width / 2, height - 40, 'Press SPACE to play again', {
      fontSize: '14px',
      fontFamily: 'monospace',
      color: '#666666'
    }).setOrigin(0.5)

    // Keyboard shortcuts
    this.input.keyboard?.on('keydown-SPACE', () => {
      this.scene.start('GameScene', { mode: 'single' })
    })

    this.input.keyboard?.on('keydown-ESC', () => {
      this.scene.start('MenuScene')
    })
  }

  private createButton(
    x: number,
    y: number,
    text: string,
    color: string,
    onClick: () => void
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y)

    const bg = this.add.rectangle(0, 0, 200, 50, 0x252535)
    bg.setStrokeStyle(2, Phaser.Display.Color.HexStringToColor(color).color)
    bg.setInteractive({ useHandCursor: true })

    const label = this.add.text(0, 0, text, {
      fontSize: '18px',
      fontFamily: 'monospace',
      color: color,
      fontStyle: 'bold'
    })
    label.setOrigin(0.5)

    container.add([bg, label])

    bg.on('pointerover', () => {
      bg.setFillStyle(0x353545)
    })

    bg.on('pointerout', () => {
      bg.setFillStyle(0x252535)
    })

    bg.on('pointerdown', onClick)

    return container
  }
}
